/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import selectSportsbookState from '../../sportsbook-state-selector';
import { RootState, ThunkAction } from '../../../../../store/types';
import { loadScript } from '~/utils/globals';
import { SPORTRADAR_SCRIPT_LOCALE_ATTRIBUTE } from '../../utils/sportradar-utils';
import reportSportsbookError from '~/components/sportsbook/lobby/utils/sportsbook-error-reporter';
import { createSelector } from 'reselect';

type State = {
	error: Error | null;
	isLoading: boolean;
	isInitialized: boolean;
};

const initialState: State = {
	isLoading: true,
	error: null,
	isInitialized: false
};

const sportradarWidgetSlice = createSlice({
	name: 'sportradarWidget',
	initialState,
	reducers: {
		loadWidgetScriptFailed: (state, action: PayloadAction<{ error: Error }>) => {
			state.error = action.payload.error;
			state.isLoading = false;
			state.isInitialized = true;
		},
		loadWidgetScriptSuccess: state => {
			state.error = null;
			state.isLoading = false;
			state.isInitialized = true;
		}
	}
});

const { loadWidgetScriptFailed, loadWidgetScriptSuccess } = sportradarWidgetSlice.actions;

export const selectIsWidgetScriptLoading = (state: RootState) =>
	selectSportsbookState(state).sportradarWidget.isLoading;
export const selectHasWidgetScriptError = (state: RootState) => !!selectSportsbookState(state).sportradarWidget.error;
export const selectIsWidgetScriptInitialized = (state: RootState) =>
	selectSportsbookState(state).sportradarWidget.isInitialized;

export const selectisWidgetScriptLoaded = createSelector(
	[selectIsWidgetScriptLoading, selectHasWidgetScriptError, selectIsWidgetScriptInitialized],
	(isLoading, hasError, isInitialized) => !isLoading && !hasError && isInitialized
);

export const loadWidgetScript =
	(widgetScriptUrl: string, sportradarLocale: string): ThunkAction =>
	async (dispatch, getState) => {
		const isScriptAlreadyLoaded = selectisWidgetScriptLoaded(getState());

		if (isScriptAlreadyLoaded) {
			return;
		}

		try {
			await loadScript(widgetScriptUrl, {
				async: true,
				[SPORTRADAR_SCRIPT_LOCALE_ATTRIBUTE]: sportradarLocale
			});

			dispatch(loadWidgetScriptSuccess());
		} catch (error) {
			if (error instanceof Error) {
				dispatch(loadWidgetScriptFailed({ error }));
				reportSportsbookError('Failed to load Sportradar Widget script', error);
			}
		}
	};

export default sportradarWidgetSlice.reducer;
